import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { GET_MEDIA_ASSETS } from '../../graphql';
import {
  isVideoFile,
  uploadVideo,
  waitForVideo,
  type VideoInfo,
} from '../../lib/media/uploadVideo';
import { VideoCoverDialog } from '../media/VideoCoverDialog';
import { clock, fileSize, isVertical, type VideoCheck, type VideoShape } from './composerRules';

/**
 * Write a post → Video (Composer Post Types and Video spec): from Media or a new
 * upload, with what's known about it read from the file before the upload starts,
 * so every picked channel is checked straight away. Stream then makes the MP4 the
 * networks are given; a post can be written, and even sent, while it does.
 */

export interface ComposerVideo extends VideoShape {
  key: string;
  assetId: string | null;
  name: string;
  status: 'uploading' | 'processing' | 'ready' | 'rejected';
  pct: number;
  /** What plays here: the file being uploaded, or the MP4. */
  src: string | null;
  /** The MP4, once it exists; or the address of a video that isn't in Media. */
  url: string | null;
  posterUrl: string | null;
  info: VideoInfo | null;
}

type LibraryVideo = {
  id: string;
  originalFilename: string | null;
  bytes: number | null;
  video: (VideoInfo & { sizeBytes?: number | null }) | null;
};

/** A video from Media as the composer holds it. */
export function videoFromLibrary(asset: LibraryVideo): ComposerVideo {
  const v = asset.video as VideoInfo & { sizeBytes?: number | null };
  const ready = v.status === 'ready' && Boolean(v.mp4Url);
  return {
    key: `asset-${asset.id}`,
    assetId: asset.id,
    name: asset.originalFilename || 'Video',
    status: ready ? 'ready' : 'processing',
    pct: 100,
    durationSeconds: v.durationSeconds,
    width: v.width,
    height: v.height,
    sizeBytes: v.sizeBytes ?? asset.bytes ?? null,
    src: v.mp4Url,
    url: v.mp4Url,
    posterUrl: v.posterUrl,
    info: v,
  };
}

/** Length and shape from the file itself: nothing is sent to learn them. */
function readFile(file: File, src: string) {
  return new Promise<Pick<VideoShape, 'durationSeconds' | 'width' | 'height'>>((resolve) => {
    const el = document.createElement('video');
    const finish = (found?: Partial<VideoShape>) => {
      clearTimeout(timer);
      resolve({
        durationSeconds: found?.durationSeconds ?? null,
        width: found?.width ?? null,
        height: found?.height ?? null,
      });
    };
    // A format this browser can't read is checked once Stream has it.
    const timer = setTimeout(() => finish(), 8000);
    el.preload = 'metadata';
    el.muted = true;
    el.onloadedmetadata = () =>
      finish({
        durationSeconds: Number.isFinite(el.duration) ? Math.round(el.duration) : null,
        width: el.videoWidth || null,
        height: el.videoHeight || null,
      });
    el.onerror = () => finish();
    el.src = src;
  });
}

const CHECK_ICON = { fail: '✕', warn: '!', ok: '✓' };

export function VideoField({
  accountId,
  video,
  onChange,
  minutesLeft,
  onUploaded,
  checks,
  onLeaveOut,
}: {
  accountId: string;
  video: ComposerVideo | null;
  onChange: (
    next: ComposerVideo | null | ((was: ComposerVideo | null) => ComposerVideo | null)
  ) => void;
  /** Minutes the plan can still add; null when there's no cap. */
  minutesLeft: number | null;
  onUploaded: () => void;
  checks: Array<VideoCheck & { key: string; name: string }>;
  /** Null where channels can't be left out (the side panel holds one). */
  onLeaveOut: ((key: string, name: string) => void) | null;
}) {
  const apollo = useApolloClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [library, setLibrary] = useState(false);
  const [error, setError] = useState('');
  const [coverOpen, setCoverOpen] = useState(false);
  const { data, loading } = useQuery<{ mediaAssets: { assets: LibraryVideo[] } }>(
    GET_MEDIA_ASSETS,
    {
      variables: { accountId, limit: 200, offset: 0 },
      skip: !library,
      fetchPolicy: 'cache-and-network',
    }
  );
  const videos = useMemo(
    () =>
      (data?.mediaAssets?.assets || []).filter(
        (a) => a.video && a.video.status !== 'error' && a.video.status !== 'rejected'
      ),
    [data]
  );

  // Only the video still on the post is updated.
  const update = (key: string, patch: Partial<ComposerVideo>) =>
    onChange((was) => (was && was.key === key ? { ...was, ...patch } : was));

  const whenSettled = (key: string, assetId: string) => {
    void waitForVideo(apollo, accountId, assetId, {
      needMp4: true,
      onStatus: (v) =>
        update(key, {
          durationSeconds: v.durationSeconds,
          width: v.width,
          height: v.height,
          posterUrl: v.posterUrl,
          info: v,
        }),
    })
      .then((v) =>
        onChange((was) =>
          was && was.key === key
            ? { ...was, status: 'ready', url: v.mp4Url, src: was.src || v.mp4Url, info: v }
            : was
        )
      )
      .catch((err) => {
        // Slow isn't failed: the post, if sent, still waits on the server.
        if (/still processing/i.test(String(err?.message))) return;
        onChange((was) => (was && was.key === key ? null : was));
        setError(err instanceof Error ? err.message : 'The video couldn’t be prepared.');
      });
  };

  // A video reopened or picked while Stream is still at it.
  const waitingOn = video?.status === 'processing' ? video : null;
  const watched = useRef<string | null>(null);
  useEffect(() => {
    if (!waitingOn?.assetId || watched.current === waitingOn.key) return;
    watched.current = waitingOn.key;
    whenSettled(waitingOn.key, waitingOn.assetId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingOn?.key, waitingOn?.assetId]);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    if (!isVideoFile(file)) {
      setError('That isn’t a video file. MP4, MOV or WebM.');
      return;
    }
    const src = URL.createObjectURL(file);
    const key = `upload-${Date.now()}`;
    const shape = await readFile(file, src);
    const over =
      minutesLeft != null &&
      shape.durationSeconds != null &&
      shape.durationSeconds > minutesLeft * 60;
    onChange({
      key,
      assetId: null,
      name: file.name,
      status: over ? 'rejected' : 'uploading',
      pct: 0,
      ...shape,
      sizeBytes: file.size,
      src,
      url: null,
      posterUrl: null,
      info: null,
    });
    setLibrary(false);
    if (over) return;
    try {
      await uploadVideo(apollo, accountId, file, (pct) => update(key, { pct }), {
        onStart: (assetId) => update(key, { assetId }),
      });
      update(key, { status: 'processing', pct: 100 });
      onUploaded();
    } catch (err) {
      onChange((was) => (was && was.key === key ? null : was));
      setError(err instanceof Error ? err.message : 'The upload stopped. Try again.');
    }
  };

  const meta = (() => {
    if (!video) return '';
    if (video.status === 'rejected') {
      return `This video is ${clock(video.durationSeconds)}. Your plan has ${minutesLeft} video minute${minutesLeft === 1 ? '' : 's'} left.`;
    }
    if (video.status === 'uploading') {
      return [`Uploading ${video.pct}%`, clock(video.durationSeconds), fileSize(video.sizeBytes)]
        .filter(Boolean)
        .join(' · ');
    }
    if (video.status === 'processing')
      return 'Preparing for social, usually a minute or two. Keep writing.';
    return [
      clock(video.durationSeconds),
      video.width && video.height ? `${video.width}×${video.height}` : '',
      fileSize(video.sizeBytes),
    ]
      .filter(Boolean)
      .join(' · ');
  })();

  const coverWords = (() => {
    const info = video?.info;
    if (info?.posterAssetId) return 'Cover · an image from Media';
    return `Cover · frame at ${clock(info?.posterTimeSeconds ?? 0)}`;
  })();

  return (
    <div className="ink-site-label wp-video">
      <span className="wp-video-head">
        <span>Video</span>
        {minutesLeft != null ? (
          <span className="ink-muted wp-video-minutes">
            {minutesLeft} video minute{minutesLeft === 1 ? '' : 's'} left on your plan
          </span>
        ) : null}
      </span>
      <input
        ref={fileRef}
        type="file"
        accept="video/*"
        hidden
        onChange={(e) => {
          void upload(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {video && !library ? (
        <div className="wp-vid" data-rejected={video.status === 'rejected' ? 'true' : undefined}>
          <div className="wp-vid-row">
            {video.src ? (
              <video
                className="wp-vid-thumb"
                src={video.src}
                muted
                playsInline
                preload="metadata"
              />
            ) : video.posterUrl ? (
              <img className="wp-vid-thumb" src={video.posterUrl} alt="" />
            ) : (
              <span className="wp-vid-thumb wp-vid-thumb-empty" aria-hidden="true" />
            )}
            <div className="wp-img-text">
              <div>{video.name}</div>
              <div className={video.status === 'rejected' ? 'wp-vid-bad' : 'ink-muted'}>{meta}</div>
            </div>
            <button type="button" className="ink-quiet-link" onClick={() => setLibrary(true)}>
              Replace
            </button>
            <button
              type="button"
              className="ink-quiet-link"
              onClick={() => {
                onChange(null);
                setError('');
              }}
            >
              Remove
            </button>
          </div>
          {video.status === 'uploading' || video.status === 'processing' ? (
            <div
              className="wp-vid-bar"
              role="progressbar"
              aria-valuenow={video.pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div style={{ width: `${video.status === 'processing' ? 100 : video.pct}%` }} />
            </div>
          ) : null}
          {video.status === 'ready' && video.assetId && video.info ? (
            <div className="wp-vid-cover">
              <span>{coverWords}</span>
              <button type="button" className="ink-link-btn" onClick={() => setCoverOpen(true)}>
                Change cover
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {!video && !library ? (
        <div className="wp-img-empty wp-vid-empty">
          <div>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => setLibrary(true)}
            >
              Choose from Media
            </button>
            <button type="button" className="ink-btn" onClick={() => fileRef.current?.click()}>
              Upload video
            </button>
          </div>
          <span className="ink-muted aoe-help">
            MP4, MOV or WebM. Checked against each channel as soon as you pick it.
          </span>
        </div>
      ) : null}
      {library ? (
        <div className="wp-lib">
          <div className="wp-lib-head">
            <span>Media · videos</span>
            <button type="button" className="ink-btn" onClick={() => fileRef.current?.click()}>
              Upload
            </button>
            <button
              type="button"
              className="ink-quiet-link"
              aria-label="Close media library"
              onClick={() => setLibrary(false)}
            >
              ×
            </button>
          </div>
          <div className="wp-lib-grid">
            {videos.map((a) => {
              const v = a.video as VideoInfo & { sizeBytes?: number | null };
              const preparing = !(v.status === 'ready' && v.mp4Url);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    onChange(videoFromLibrary(a));
                    setLibrary(false);
                    setError('');
                  }}
                >
                  <span className="wp-vid-tile">
                    {v.posterUrl ? <img src={v.posterUrl} alt="" loading="lazy" /> : null}
                    {preparing ? <span className="wp-vid-preparing">Preparing</span> : null}
                    {v.durationSeconds != null ? (
                      <span className="wp-vid-dur">{clock(v.durationSeconds)}</span>
                    ) : null}
                  </span>
                  <span>{a.originalFilename || 'Video'}</span>
                  <span className="ink-muted wp-vid-shape">
                    {[
                      v.width && v.height ? (isVertical(v) ? 'Vertical' : 'Horizontal') : '',
                      fileSize(v.sizeBytes ?? a.bytes),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </button>
              );
            })}
            {!videos.length ? (
              <span className="ink-muted">{loading ? 'Loading…' : 'No videos in Media yet.'}</span>
            ) : null}
          </div>
        </div>
      ) : null}
      {error ? (
        <span className="aoe-help" style={{ color: 'var(--ink-red)' }}>
          {error}
        </span>
      ) : null}
      {checks.length && !library ? (
        <div className="wp-checks">
          <div className="wp-checks-head">CHECKED FOR EACH CHANNEL</div>
          {checks.map((c) => (
            <div key={c.key} className="wp-check" data-state={c.state}>
              <span className="wp-check-icon" aria-hidden="true">
                {CHECK_ICON[c.state]}
              </span>
              <span className="wp-check-net">{c.name}</span>
              <span className="wp-check-msg">{c.message}</span>
              {c.state === 'fail' && onLeaveOut ? (
                <button
                  type="button"
                  className="ink-link-btn"
                  onClick={() => onLeaveOut(c.key, c.name)}
                >
                  Leave out
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {coverOpen && video?.assetId && video.info ? (
        <VideoCoverDialog
          accountId={accountId}
          assetId={video.assetId}
          name={video.name}
          video={video.info}
          images={[]}
          onClose={() => setCoverOpen(false)}
          onSaved={() => {
            setCoverOpen(false);
            watched.current = null;
            void apollo.refetchQueries({ include: ['GetMediaAssets'] });
          }}
        />
      ) : null}
    </div>
  );
}
